import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, Info, Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import LazyTradingViewWidget from './LazyTradingViewWidget';

const PortfolioSimulator = () => {
  const navigate = useNavigate();
  const [investment, setInvestment] = useState(50000);
  const [investmentInput, setInvestmentInput] = useState('50000');
  const [years, setYears] = useState(10);
  const [includeInflation, setIncludeInflation] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("livretGoldStandard");
  const isMobile = useIsMobile();

  // Animation control
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setVisible(true);
      }
    }, {
      threshold: 0.2
    });
    const element = document.getElementById('portfolio-simulator');
    if (element) observer.observe(element);
    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  // Handle investment input changes
  const handleInvestmentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setInvestmentInput(value);
  };

  const handleInvestmentInputBlur = () => {
    let value = parseInt(investmentInput);
    if (isNaN(value)) value = 10000;
    if (value < 10000) value = 10000;
    if (value > 1000000) value = 1000000;
    setInvestment(value);
    setInvestmentInput(value.toString());
  };

  // Update input when slider changes
  useEffect(() => {
    setInvestmentInput(investment.toString());
  }, [investment]);
  
  useEffect(() => {
    // Generate portfolio projection data
    const generateData = () => {
      // Standard rates
      let rate = 0.05; // Default rate

      // Apply Livret Gold rates if selected and investment amount is valid
      if (selectedProduct === "livretGoldStandard" && investment >= 10000 && investment <= 49999) {
        rate = 0.0812; // 8.12% for Livret Gold Standard - Updated from 8.22%
      } else if (selectedProduct === "livretGoldPremium" && investment >= 50000 && investment <= 199999) {
        rate = 0.0922; // 9.22% for Livret Gold Premium
      } else if (selectedProduct === "livretGoldGold" && investment >= 200000 && investment <= 1000000) {
        rate = 0.1109; // 11.09% for Livret Gold Gold
      }
      // Apply Hydrogène rates if selected and investment amount is valid
      // Updated minimum for Hydrogen Standard from 20,000€ to 50,000€
      else if (selectedProduct === "hydrogenStandard" && investment >= 50000 && investment <= 99999) {
        rate = 0.12; // 12% for Hydrogène Standard
      } else if (selectedProduct === "hydrogenPremium" && investment >= 100000 && investment <= 149999) {
        rate = 0.15; // 15% for Hydrogène Premium
      } else if (selectedProduct === "hydrogenGold" && investment >= 150000 && investment <= 1000000) {
        rate = 0.168; // 16.8% for Hydrogène Gold
      }
      const inflation = includeInflation ? 0.02 : 0; // 2% annual inflation

      const newData = [];
      const currentYear = new Date().getFullYear(); // Starting year

      for (let year = 0; year <= years; year++) {
        const value = investment * Math.pow(1 + rate - inflation, year);
        newData.push({
          year: `${currentYear + year}`,
          value: Math.round(value)
        });
      }
      setData(newData);
    };
    generateData();
  }, [investment, years, includeInflation, isMobile, selectedProduct]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };
  const renderTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white p-3 shadow-lg border border-gray-100 rounded">
          <p className="font-medium">{label}</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-3 h-3 rounded" style={{
            backgroundColor: selectedProduct.startsWith('hydrogen') ? "#3B82F6" : "#D4AF37"
          }} />
            <span className="font-medium">{formatCurrency(payload[0].value)}</span>
          </div>
        </div>;
    }
    return null;
  };

  // Check if Livret Gold options are available based on investment amount
  const isLivretGoldStandardAvailable = investment >= 10000 && investment <= 49999;
  const isLivretGoldPremiumAvailable = investment >= 50000 && investment <= 199999;
  const isLivretGoldGoldAvailable = investment >= 200000 && investment <= 1000000;
  // Update the Hydrogen Standard minimum from 20,000€ to 50,000€
  const isHydrogenStandardAvailable = investment >= 50000 && investment <= 99999;
  const isHydrogenPremiumAvailable = investment >= 100000 && investment <= 149999;
  const isHydrogenGoldAvailable = investment >= 150000 && investment <= 1000000;

  // Get label for selected product
  const getProductLabel = () => {
    switch (selectedProduct) {
      case "livretGoldStandard":
        return "Livret Gold (Pack Standard)";
      case "livretGoldPremium":
        return "Livret Gold (Pack Premium)";
      case "livretGoldGold":
        return "Livret Gold (Pack Gold)";
      case "hydrogenStandard":
        return "Hydrogène (Pack Standard)";
      case "hydrogenPremium":
        return "Hydrogène (Pack Premium)";
      case "hydrogenGold":
        return "Hydrogène (Pack Gold)";
      default:
        return "";
    }
  };

  // Get current strategy value from latest data
  const getCurrentValue = (): number => {
    if (data.length === 0) return 0;
    const latestData = data[data.length - 1];
    return latestData.value || 0;
  };
  
  const handleRedirectToForm = () => {
    navigate('/bilan-patrimonial');
  };

  // Get TradingView widget type and symbol based on selected product
  const getTradingViewConfig = () => {
    if (selectedProduct.startsWith('hydrogen')) {
      return {
        type: 'news' as const
      };
    } else {
      return {
        type: 'chart' as const,
        symbol: 'TVC:GOLD'
      };
    }
  };

  return <section id="portfolio-simulator" className={cn("py-10 md:py-20 px-4 md:px-6 bg-gradient-to-b from-gray-50 to-white transition-opacity duration-1000 ease-in-out", visible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-semibold relative inline-block">
            Simulez la croissance de votre patrimoine
            <div className="absolute h-1 w-1/2 bg-patrimoine-gold bottom-[-12px] left-1/4 rounded"></div>
          </h2>
          <p className="text-patrimoine-muted max-w-2xl mx-auto mt-4 text-base md:text-lg">
            Visualisez comment votre patrimoine peut évoluer selon différentes stratégies d'investissement
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
          <Card className="lg:col-span-1 shadow-md hover:shadow-lg transition-shadow duration-300 border-t-4 border-t-patrimoine-gold">
            <CardContent className={`${isMobile ? 'p-4' : 'pt-6'}`}>
              <div className="space-y-6 md:space-y-8">
                <div>
                  <h3 className="text-lg md:text-xl font-serif mb-2 text-patrimoine-dark flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-patrimoine-gold" />
                    Montant initial
                  </h3>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-patrimoine-muted">Investissement</span>
                    <div className="flex items-center">
                      <Input
                        type="text"
                        value={investmentInput}
                        onChange={handleInvestmentInputChange}
                        onBlur={handleInvestmentInputBlur}
                        className="w-24 h-8 text-right mr-1 text-patrimoine-dark font-medium"
                        aria-label="Montant d'investissement"
                      />
                      <span className="font-medium text-patrimoine-dark">€</span>
                    </div>
                  </div>
                  <Slider 
                    value={[investment]} 
                    onValueChange={values => setInvestment(values[0])} 
                    min={10000} 
                    max={1000000} 
                    step={10000} 
                    className="my-4" 
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>10 000 €</span>
                    <span>1 000 000 €</span>
                  </div>
                </div>
                
                
                <div>
                  <h3 className="text-lg md:text-xl font-serif mb-2 text-patrimoine-dark">Horizon d'investissement</h3>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-patrimoine-muted">Durée</span>
                    <span className="font-medium text-patrimoine-dark">{years} {years === 1 ? 'AN' : 'ans'}</span>
                  </div>
                  <Slider value={[years]} onValueChange={values => setYears(values[0])} min={1} max={30} step={1} className="my-4" />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>1 AN</span>
                    <span>30 ans</span>
                  </div>
                </div>
                
                
                <div>
                  <h3 className="text-lg md:text-xl font-serif mb-2 text-patrimoine-dark flex items-center gap-2">
                    <Coins className="h-5 w-5 text-patrimoine-gold" />
                    Produit d'investissement
                  </h3>
                  <RadioGroup value={selectedProduct} onValueChange={setSelectedProduct} className="my-3">
                    {/* Livret Gold - Packs */}
                    <div className="mt-2 mb-3">
                      <h4 className="text-sm font-medium mb-2 text-patrimoine-gold">Livret Gold</h4>
                      
                      {/* Pack Standard - Updated minimum to 10K */}
                      <div className="flex items-center space-x-2 mt-3">
                        <RadioGroupItem value="livretGoldStandard" id="livretGoldStandard" disabled={!isLivretGoldStandardAvailable} />
                        <div>
                          <label htmlFor="livretGoldStandard" className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${isLivretGoldStandardAvailable ? '' : 'text-gray-400'}`}>
                            Pack Standard
                          </label>
                          <p className={`text-xs mt-0.5 ${isLivretGoldStandardAvailable ? 'text-patrimoine-muted' : 'text-gray-400'}`}>Taux à 8,12% brut/an (10 000€ à 49 999€)</p>
                        </div>
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-gray-400 cursor-help ml-1" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-100 rounded text-sm">
                              <p>Le Livret Gold Pack Standard est disponible uniquement pour des investissements entre 10 000€ et 49 999€.</p>
                              <p className="mt-2">Taux de rendement brut annuel: 8,12%</p>
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      </div>
                      
                      {/* Pack Premium */}
                      <div className="flex items-center space-x-2 mt-3">
                        <RadioGroupItem value="livretGoldPremium" id="livretGoldPremium" disabled={!isLivretGoldPremiumAvailable} />
                        <div>
                          <label htmlFor="livretGoldPremium" className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${isLivretGoldPremiumAvailable ? '' : 'text-gray-400'}`}>
                            Pack Premium
                          </label>
                          <p className={`text-xs mt-0.5 ${isLivretGoldPremiumAvailable ? 'text-patrimoine-muted' : 'text-gray-400'}`}>Taux à 9,22% brut/an (50 000€ à 199 999€)</p>
                        </div>
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-gray-400 cursor-help ml-1" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-100 rounded text-sm">
                              <p>Le Livret Gold Pack Premium est disponible uniquement pour des investissements entre 50 000€ et 199 999€.</p>
                              <p className="mt-2">Taux de rendement brut annuel: 9,22%</p>
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      </div>
                      
                      {/* Pack Gold */}
                      <div className="flex items-center space-x-2 mt-3">
                        <RadioGroupItem value="livretGoldGold" id="livretGoldGold" disabled={!isLivretGoldGoldAvailable} />
                        <div>
                          <label htmlFor="livretGoldGold" className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${isLivretGoldGoldAvailable ? '' : 'text-gray-400'}`}>
                            Pack Gold
                          </label>
                          <p className={`text-xs mt-0.5 ${isLivretGoldGoldAvailable ? 'text-patrimoine-muted' : 'text-gray-400'}`}>Taux à 11,09% brut/an (200 000€ à 1 000 000€)</p>
                        </div>
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-gray-400 cursor-help ml-1" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-100 rounded text-sm">
                              <p>Le Livret Gold Pack Gold est disponible uniquement pour des investissements entre 200 000€ et 1 000 000€.</p>
                              <p className="mt-2">Taux de rendement brut annuel: 11,09%</p>
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                    
                    {/* Hydrogène - Packs */}
                    <div className="mt-6 mb-3">
                      <h4 className="text-sm font-medium mb-2 text-blue-600">Hydrogène</h4>
                      
                      {/* Pack Standard - Updated minimum from 20K to 50K */}
                      <div className="flex items-center space-x-2 mt-3">
                        <RadioGroupItem value="hydrogenStandard" id="hydrogenStandard" disabled={!isHydrogenStandardAvailable} />
                        <div>
                          <label htmlFor="hydrogenStandard" className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${isHydrogenStandardAvailable ? '' : 'text-gray-400'}`}>
                            Pack Standard
                          </label>
                          <p className={`text-xs mt-0.5 ${isHydrogenStandardAvailable ? 'text-patrimoine-muted' : 'text-gray-400'}`}>Taux à 12% brut/an (50 000€ à 99 999€)</p>
                        </div>
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-gray-400 cursor-help ml-1" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-100 rounded text-sm">
                              <p>L'investissement Hydrogène Pack Standard est disponible uniquement pour des investissements entre 50 000€ et 99 999€.</p>
                              <p className="mt-2">Taux de rendement brut annuel: 12%</p>
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      </div>
                      
                      {/* Pack Premium */}
                      <div className="flex items-center space-x-2 mt-3">
                        <RadioGroupItem value="hydrogenPremium" id="hydrogenPremium" disabled={!isHydrogenPremiumAvailable} />
                        <div>
                          <label htmlFor="hydrogenPremium" className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${isHydrogenPremiumAvailable ? '' : 'text-gray-400'}`}>
                            Pack Premium
                          </label>
                          <p className={`text-xs mt-0.5 ${isHydrogenPremiumAvailable ? 'text-patrimoine-muted' : 'text-gray-400'}`}>Taux à 15% brut/an (100 000€ à 149 999€)</p>
                        </div>
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-gray-400 cursor-help ml-1" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-100 rounded text-sm">
                              <p>L'investissement Hydrogène Pack Premium est disponible uniquement pour des investissements entre 100 000€ et 149 999€.</p>
                              <p className="mt-2">Taux de rendement brut annuel: 15%</p>
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      </div>
                      
                      {/* Pack Gold */}
                      <div className="flex items-center space-x-2 mt-3">
                        <RadioGroupItem value="hydrogenGold" id="hydrogenGold" disabled={!isHydrogenGoldAvailable} />
                        <div>
                          <label htmlFor="hydrogenGold" className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${isHydrogenGoldAvailable ? '' : 'text-gray-400'}`}>
                            Pack Gold
                          </label>
                          <p className={`text-xs mt-0.5 ${isHydrogenGoldAvailable ? 'text-patrimoine-muted' : 'text-gray-400'}`}>Taux à 16,8% brut/an (150 000€ à 1 000 000€)</p>
                        </div>
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-gray-400 cursor-help ml-1" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-100 rounded text-sm">
                              <p>L'investissement Hydrogène Pack Gold est disponible uniquement pour des investissements entre 150 000€ et 1 000 000€.</p>
                              <p className="mt-2">Taux de rendement brut annuel: 16,8%</p>
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </RadioGroup>
                  
                  {/* Display error messages if product is not available for current investment amount */}
                  {selectedProduct === "livretGoldStandard" && !isLivretGoldStandardAvailable && <p className="text-xs text-amber-600 mt-1">
                      Le Livret Gold Pack Standard nécessite un investissement entre 10 000€ et 49 999€.
                    </p>}
                  {selectedProduct === "livretGoldPremium" && !isLivretGoldPremiumAvailable && <p className="text-xs text-amber-600 mt-1">
                      Le Livret Gold Pack Premium nécessite un investissement entre 50 000€ et 199 999€.
                    </p>}
                  {selectedProduct === "livretGoldGold" && !isLivretGoldGoldAvailable && <p className="text-xs text-amber-600 mt-1">
                      Le Livret Gold Pack Gold nécessite un investissement entre 200 000€ et 1 000 000€.
                    </p>}
                  {selectedProduct === "hydrogenStandard" && !isHydrogenStandardAvailable && <p className="text-xs text-amber-600 mt-1">
                      L'investissement Hydrogène Pack Standard nécessite un investissement entre 50 000€ et 99 999€.
                    </p>}
                  {selectedProduct === "hydrogenPremium" && !isHydrogenPremiumAvailable && <p className="text-xs text-amber-600 mt-1">
                      L'investissement Hydrogène Pack Premium nécessite un investissement entre 100 000€ et 149 999€.
                    </p>}
                  {selectedProduct === "hydrogenGold" && !isHydrogenGoldAvailable && <p className="text-xs text-amber-600 mt-1">
                      L'investissement Hydrogène Pack Gold nécessite un investissement entre 150 000€ et 1 000 000€.
                    </p>}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={includeInflation} 
                      onCheckedChange={setIncludeInflation} 
                      aria-label="Inclure l'inflation"
                      id="inflation-toggle"
                    />
                    <label htmlFor="inflation-toggle" className="text-sm">Inclure l'inflation</label>
                  </div>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 cursor-help" aria-label="Information sur l'inflation" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-100 rounded text-sm">
                        <p>L'inflation réduit le pouvoir d'achat de votre argent au fil du temps.</p>
                        <p className="mt-2">Nous utilisons un taux d'inflation annuel de 2% pour ajuster les projections de rendement.</p>
                        <p className="mt-2 text-patrimoine-muted">Activer cette option montre la valeur réelle de vos investissements.</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
                
                <Button className="w-full cta-button group" onClick={handleRedirectToForm}>
                  Obtenez votre stratégie personnalisée
                </Button>
              </div>
            </CardContent>
          </Card>
          
          
          <Card className="lg:col-span-2 shadow-md hover:shadow-lg transition-shadow duration-300">
            <CardContent className={`${isMobile ? 'p-4' : 'pt-6'}`}>
              <h3 className="text-lg md:text-xl font-serif mb-4 text-patrimoine-dark">
                Projection de croissance sur {years} ans
                {selectedProduct === "livretGoldStandard" && isLivretGoldStandardAvailable || selectedProduct === "livretGoldPremium" && isLivretGoldPremiumAvailable || selectedProduct === "livretGoldGold" && isLivretGoldGoldAvailable || selectedProduct === "hydrogenStandard" && isHydrogenStandardAvailable || selectedProduct === "hydrogenPremium" && isHydrogenPremiumAvailable || selectedProduct === "hydrogenGold" && isHydrogenGoldAvailable ? <span className={`text-sm ml-2 ${selectedProduct.startsWith('hydrogen') ? 'text-blue-600' : 'text-patrimoine-gold'}`}>
                    ({getProductLabel()})
                  </span> : null}
              </h3>
              
              <div className={`${isMobile ? 'h-[300px]' : 'h-[400px]'} w-full`}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data} margin={{
                  top: 10,
                  right: isMobile ? 5 : 30,
                  left: isMobile ? 0 : 20,
                  bottom: isMobile ? 35 : 50
                }}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={selectedProduct.startsWith('hydrogen') ? "#3B82F6" : "#D4AF37"} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={selectedProduct.startsWith('hydrogen') ? "#3B82F6" : "#D4AF37"} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="year" 
                      tick={{
                        fontSize: isMobile ? 10 : 12
                      }} 
                      tickMargin={10} 
                      // Remove the tickFormatter to display the full year
                    />
                    <YAxis tickFormatter={value => `${value / 1000}k€`} tick={{
                    fontSize: isMobile ? 10 : 12
                  }} width={isMobile ? 30 : 45} />
                    <RechartsTooltip content={renderTooltip} />
                    <Area type="monotone" dataKey="value" stroke={selectedProduct.startsWith('hydrogen') ? "#3B82F6" : "#D4AF37"} fillOpacity={1} fill="url(#colorValue)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {/* TradingView Widget Container */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 col-span-1 md:col-span-2">
                  <h4 className="text-sm font-medium mb-4">
                    {selectedProduct.startsWith('hydrogen') ? "Actualités marché" : "Cours de l'or en direct"}
                  </h4>
                  <div className="h-[300px] w-full">
                    <LazyTradingViewWidget {...getTradingViewConfig()} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
};
export default PortfolioSimulator;
